import React from 'react';

import { useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';

import { ReservationLimitInPeriodContext } from '.';
import { useVenueListReservationsCountQuery } from '../../hooks/reactQuery/reservations/useVenueListReservationsCountQuery';
import { useAppSelector } from '../../redux/hooks';
import { generateSubscriptionPeriods } from '../../utils/subscription';
import { VENUE_MANUAL_TAGS } from '../../utils/venue-tags';

export const ReservationsLimitInPeriodProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const queryClient = useQueryClient();
  const venueState = useAppSelector((state) => state.venue.value);
  const { subscription, isLoading } = useAppSelector((state) => state.subscription.value);

  const startDate =
    !isLoading && !!subscription && venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)
      ? dayjs
          .utc(subscription?.expirationDate)
          .subtract(1, 'month')
          .toISOString()
      : !isLoading && !!subscription && venueState?.tags?.includes(VENUE_MANUAL_TAGS.VenuePlans)
        ? generateSubscriptionPeriods(
            new Date(subscription?.createdAt as string),
            new Date(subscription?.expirationDate as string),
            { currentOnly: true },
          )[0]?.start.toISOString()
        : dayjs.utc().startOf('month').toISOString();

  const endDate =
    !isLoading && !!subscription && venueState?.tags?.includes(VENUE_MANUAL_TAGS.BrandPlans)
      ? subscription?.expirationDate
      : !isLoading && !!subscription && venueState?.tags?.includes(VENUE_MANUAL_TAGS.VenuePlans)
        ? generateSubscriptionPeriods(
            new Date(subscription?.createdAt as string),
            new Date(subscription?.expirationDate as string),
            { currentOnly: true },
          )[0]?.end.toISOString()
        : venueState?.tags?.includes(VENUE_MANUAL_TAGS.VenuePlans)
          ? dayjs.utc().endOf('month').toISOString()
          : '';

  const { data: reservationsCount } = useVenueListReservationsCountQuery(
    {
      venueID: venueState?.id || '',
      subscriptionDates: {
        start: startDate,
        end: endDate || '',
      },
      privateOffer: false,
    },
    !!startDate && !!endDate && !!venueState,
  );

  const getReservationsLimitInPeriodHandle = async () => {
    await queryClient.invalidateQueries({ queryKey: ['useVenueListReservationsCountAPI'] });
  };

  return (
    <ReservationLimitInPeriodContext.Provider
      value={{
        totalWaitingAndShippedReservations:
          (reservationsCount?.totalShippedWithoutInvite || 0) +
          (reservationsCount?.totalWatingShippingWithoutInvite || 0),
        totalReservedAndCheckedInReservations:
          (reservationsCount?.totalReservedWithoutInvite || 0) +
          (reservationsCount?.totalCheckedInWithoutInvite || 0),
        totalCompletedPromotions: reservationsCount?.totalCompletedPromotions || 0,
        getReservationsLimitInPeriodHandle,
      }}
    >
      {children}
    </ReservationLimitInPeriodContext.Provider>
  );
};
